import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { GIButton } from 'app-components';
import states from '@constants/configs/states';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import { capitalize } from '@utils/miscellaneous/objectHelpers';
import { ROOT_STATE } from '@store/store.types';
import { calculateHouseholdNumber, getCitizenshipOrVlpStatusMessageId } from './utils/utility';
import { ScreenType, StateSubsidyModel } from './types/apiResponse';

const NA_MESSAGE_ID = 'member.enrollment.modal.cancelTerminate.NA';

const safeCapitalize = (value: string | null | undefined): React.ReactNode => {
    if (value == null || value === '') {
        return <FormattedMessage id={NA_MESSAGE_ID} />;
    }
    return capitalize(value);
};

const renderYesNo = (value: any): React.ReactNode => (
    <span>
        <FormattedMessage id={value ? 'button.yes' : 'button.no'} />
    </span>
);

const hasStateSubsidyModel = (stateSubsidyModel?: StateSubsidyModel | string): boolean =>
    stateSubsidyModel != null && stateSubsidyModel !== '';

/**
 * Resolves whether the State Subsidy configuration uses the SLCSP Multiplier model.
 * This only distinguishes SLCSP_MULTIPLIER from FLAT_DOLLAR_AMOUNT when a model is
 * actually configured; a missing (null/"") model is handled upstream by
 * `hasStateSubsidyModel`, which suppresses all State Subsidy details, so it never
 * falls back to FLAT here (HIX-234502).
 */
const isSlcspMultiplierModel = (stateSubsidyModel?: StateSubsidyModel | string): boolean =>
    stateSubsidyModel === StateSubsidyModel.SLCSP_MULTIPLIER;

/**
 * Builds the columns for the Eligibility Worksheet tables.
 *
 * State Subsidy details are driven by the State Subsidy configuration returned in the
 * eligibility response (HIX-233726), replacing the previous hard-coded PA state check:
 *  - stateSubsidyApplicable === false -> no State Subsidy details are shown.
 *  - stateSubsidyModel null/"" -> no State Subsidy details are shown, even when
 *    stateSubsidyApplicable is true (HIX-234502).
 *  - When applicable, the "Eligible for State Subsidy" Yes/No column is shown on BOTH the
 *    Tax Household and Household Members tables, regardless of the model.
 *  - The model only affects the Financial Savings section of the Tax Household table:
 *    - FLAT_DOLLAR_AMOUNT (NJ, VA) -> shows the State Subsidy Amount only.
 *    - SLCSP_MULTIPLIER (PA, NM) -> replicates the original PA display: Maximum Household
 *      Contribution for State Subsidy, its percentage, and the State Subsidy Amount.
 */
export const resultColumns = (
    screenType: ScreenType | string,
    stateSubsidyModel?: StateSubsidyModel | string,
    stateSubsidyApplicable?: boolean,
    stateCode?: string,
    openModal?: (modalId: string, props?: any) => void,
) => {
    const showStateSubsidy = !!stateSubsidyApplicable && hasStateSubsidyModel(stateSubsidyModel);

    switch (screenType) {
        case ScreenType.Household:
            return [
                {
                    titleId: 'application.eligibility.household.number',
                    key: 'householdNumber',
                    render: (item: { householdNumber: string }) => calculateHouseholdNumber(item?.householdNumber),
                },
                {
                    titleId: 'application.eligibility.household.income',
                    key: 'householdIncome',
                    render: (items: { members: any; householdIncome: any }) => items?.householdIncome,
                },
                {
                    titleId: 'application.eligibility.household.size',
                    key: 'householdSize',
                },
                {
                    titleId: 'application.eligibility.household.fpl.percentage',
                    key: 'fpl',
                },
                {
                    titleId: 'application.eligibility.aptc.calculation.detailas',
                    key: 'aptcDetails',
                    render: (item: any) =>
                        mapApctDetails(item, stateSubsidyModel, stateSubsidyApplicable, stateCode, openModal),
                },
                {
                    titleId: 'application.eligibility.eligible.for.aptc',
                    key: 'eligiblieForAptc',
                    render: (items: { eligiblieForAptc: any }) => renderYesNo(items?.eligiblieForAptc),
                },
                // Both State Subsidy models surface the "Eligible for State Subsidy" Yes/No
                // column on the Tax Household table when State Subsidy is applicable.
                ...(showStateSubsidy
                    ? [
                        {
                            titleId: 'application.eligibility.eligible.for.state.subsidy',
                            key: 'eligiblieForStateSubsidy',
                            render: (items: { eligiblieForStateSubsidy: any }) =>
                                renderYesNo(items?.eligiblieForStateSubsidy),
                        },
                    ]
                    : []),
                {
                    titleId: 'application.eligibility.eligible.for.cost.sharing.reducion',
                    key: 'csrEligible',
                    render: (items: { csrEligible: any }) => renderYesNo(items?.csrEligible),
                },
            ];
        case ScreenType.Member:
            return [
                {
                    titleId: 'application.eligibility.member.idenfifier',
                    key: 'applicantGuid',
                },
                {
                    titleId: 'application.eligibility.member.name',
                    key: 'fullName',
                    render: (items: { firstName: any; lastName: any }) => `${items?.firstName} ${items?.lastName}`,
                },
                {
                    titleId: 'application.eligibility.member.dob',
                    key: 'dateOfBirth',
                },
                {
                    titleId: 'application.eligibility.member.age',
                    key: 'effectiveAge',
                    render: (items: { effectiveAge: any }) =>
                        !items.effectiveAge ? 'N/A' : `${items?.effectiveAge} Years`,
                },
                {
                    titleId: 'application.eligibility.member.citizenship',
                    key: 'citizenshipOrVlpStatus',
                    render: (items: { citizenshipOrVlpStatus: string }) => {
                        if (!items.citizenshipOrVlpStatus) {
                            return <FormattedMessage id={NA_MESSAGE_ID} />;
                        }
                        const messageId = getCitizenshipOrVlpStatusMessageId(items.citizenshipOrVlpStatus);
                        return messageId ? (
                            <FormattedMessage id={messageId} />
                        ) : (
                            items.citizenshipOrVlpStatus
                        );
                    },
                },
                {
                    titleId: 'application.eligibility.member.eligible.for.aptc',
                    key: 'aptcEligible',
                    render: (items: { aptcEligible: any }) => renderYesNo(items?.aptcEligible),
                },
                // Both State Subsidy models add the "State Subsidy" Yes/No column to the
                // Household Members table when State Subsidy is applicable.
                ...(showStateSubsidy
                    ? [
                        {
                            titleId: 'application.eligibility.eligible.for.state.subsidy',
                            key: 'stateSubsidyEligible',
                            render: (items: { stateSubsidyEligible: any }) =>
                                renderYesNo(items?.stateSubsidyEligible),
                        },
                    ]
                    : []),
                {
                    titleId: 'application.eligibility.member.cost.sharing.level',
                    key: 'csrLevel',
                    render: (items: { csrLevel: any }) => (!items.csrLevel ? 'N/A' : items.csrLevel),
                },
            ];
        case ScreenType.MedicaidMember:
            return [
                {
                    titleId: 'application.eligibility.medicaid.chip.member.name',
                    key: 'memberName',
                    render: (items: { memberName: any }) => `${items?.memberName}`,
                },
                {
                    titleId: 'application.eligibility.medicaid.chip.parameter',
                    key: 'age',
                    render: (item: any) => mapEligibilityParameters(item),
                },
                {
                    titleId: 'application.eligibility.medicaid.chip.outcome',
                    key: 'medicaid',
                    render: (item: any) => mapEligibilityOutcome(item),
                },
                {
                    titleId: 'application.eligibility.medicaid.chip.factors',
                    key: 'category',
                    render: (item: any) => mapEligibilityFactors(item),
                },
            ];
        default:
            return [];
    }
};

const mapEligibilityParameters = (item: any) => {
    const isMNFullStack = useSelector((state: ROOT_STATE) => state.common.environment.isMNFullStack);

    // Define a list of parameters to be displayed in the table
    const parameters = [
        { id: 'age', label: 'application.eligibility.medicaid.chip.parameter.age', value: item.age },
        {
            id: 'householdSize',
            label: 'application.eligibility.medicaid.chip.parameter.housesize',
            value: item.householdSize,
        },
        { id: 'income', label: 'application.eligibility.medicaid.chip.parameter.income', value: item.income },
        {
            id: 'fpl',
            label: 'application.eligibility.medicaid.chip.parameter.fpl',
            value: item.fpl != null ? `${item.fpl}%` : <FormattedMessage id={NA_MESSAGE_ID} />,
        },
        {
            id: 'citizenOrQualifiedImmigrant',
            label: 'application.eligibility.medicaid.chip.parameter.qualified.immigrant',
            value: item.citizenOrQualifiedImmigrant ? (
                <FormattedMessage id="label.yes" />
            ) : (
                <FormattedMessage id="label.no" />
            ),
        },
        {
            id: 'residency',
            label: 'application.eligibility.medicaid.chip.parameter.residency',
            value: safeCapitalize(item.residency),
        },
        {
            id: 'deniedMedicaidChipInPast90Days',
            label: 'application.eligibility.medicaid.chip.parameter.denied.medicaid',
            value: safeCapitalize(item.deniedMedicaidChipInPast90Days),
        },

        ...(isMNFullStack
            ? [
                {
                    id: 'deniedBhpInPast90Days',
                    label: 'application.eligibility.medicaid.chip.parameter.denied.bhpInPast90Days',
                    value: safeCapitalize(item.deniedBhpInPast90Days),
                },
            ]
            : []),
    ];
    // Generate table rows dynamically based on the parameters array
    const tableRows = parameters.map(param => (
        <tr key={param.id}>
            <FormattedMessage id={param.label} />
            {param.value}
        </tr>
    ));
    return (
        <table>
            <tbody>{tableRows}</tbody>
        </table>
    );
};

const mapEligibilityOutcome = (item: any) => {
    const {
        bhpasMedicaidAssessed,
        eligibilityEnableBhp,
        generateChipEligibilityEnabled: isGenerateChipEligibilityEnabled
    } = useSelector((state: any) => state.common?.environment ?? {});
        
    const result: any[] = [];
    const addRow = (id: string, content: any, isHeading = false) => {
        result.push(
            <tr>
                {' '}
                <ConditionalRendering isTrue={isHeading}>
                    <b>
                        <FormattedMessage id={id} /> {' '}
                    </b>
                </ConditionalRendering>
                <ConditionalRendering isTrue={!isHeading}>
                    <><FormattedMessage id={id} /> <FormattedMessage id={content} />{' '}</>
                </ConditionalRendering>
            </tr>,
        );
    };
    const columnValue = (value: string) =>
        value?.toUpperCase() === 'YES' ? 'common.yes' : 'common.no';
    
    // Magi Programs
    addRow('application.eligibility.medicaid.chip.outcome.magiPrograms', '', true);
    addRow('application.eligibility.medicaid.chip.outcome.medicaid', columnValue(item.medicaid));

    if (isGenerateChipEligibilityEnabled) {
        addRow('application.eligibility.medicaid.chip.outcome.chip', columnValue(item.chip));
    }
    
    if (eligibilityEnableBhp && !bhpasMedicaidAssessed) {
        addRow('application.eligibility.medicaid.chip.outcome.bhp', columnValue(item.bhp));
    }

    // Other Programs
    addRow('application.eligibility.medicaid.chip.outcome.otherPrograms', '', true);
    addRow('application.eligibility.medicaid.chip.outcome.nonMagi', columnValue(item.nonMAGI));
    addRow('application.eligibility.medicaid.chip.outcome.emergencyMedicaid', columnValue(item.emergencyMedicaid));
    return (
        <table>
            <tbody>{result}</tbody>
        </table>
    );
};

const mapEligibilityFactors = (item: any) => {
    const result: any[] = [];
    const addRowWithBulletPoint = (id: string, content: any) => {
        result.push(
            <div>
                <b>
                    <FormattedMessage id={id} />
                </b>
            </div>,
        );
        content?.split(',').map((item: string, idx: number) => {
            result.push(<li key={`text-${idx}`}>{item.trim()}</li>);
        });
    };
    //this function will add row without bullet point
    const addRow = (id: string, content: any) => {
        result.push(
            <div>
                <FormattedMessage id={id} />
            </div>,
        );
        let updatedItem = content != null && !String(content).includes('null') ? (
            String(content).trim()
        ) : (
            <FormattedMessage id={NA_MESSAGE_ID} />
        );
        result.push(updatedItem);
    };
    // Helper function to add simple text rows
    const addSimpleRow = (text: string) => {
        text?.split(',').map(item => {
            result.push(<li>{item.trim()}</li>);
        });
    };

    // Check for eligibility by category
    if (item.category !== null && item.category !== 'null') {
        addRow('application.eligibility.medicaid.chip.factors.category', item.category);
        addRow(
            'application.eligibility.medicaid.chip.factors.category.income.threshold',
            `${item.categoryIncomeThreshold}%`,
        );
    } else {
        // Various ineligible reason text checks
        const reasons = [
            {
                text: item.ineligibleReasonTextForChild,
                id: 'application.eligibility.medicaid.chip.factors.child.category',
            },
            {
                text: item.ineligibleReasonTextForPregnant,
                id: 'application.eligibility.medicaid.chip.factors.pregnant.person',
            },
            {
                text: item.ineligibleReasonTextForParentCareTaker,
                id: 'application.eligibility.medicaid.chip.factors.parent.caretaker',
            },
            {
                text: item.ineligibleReasonTextForAdultGroup,
                id: 'application.eligibility.medicaid.chip.factors.childless.adult',
            },
            {
                text: item.ineligibleReasonTextForFormerFosterCare,
                id: 'application.eligibility.medicaid.chip.factors.former.foster.care',
            },
            {
                text: item.ineligibleReasonTextForNonMagiMedicaid,
                id: 'application.eligibility.medicaid.chip.factors.non.magi.medicaid',
            },
            {
                text: item.ineligibleReasonTextForEmergencyMedicaid,
                id: 'application.eligibility.medicaid.chip.factors.emergency.medicaid',
            },
            { text: item.ineligibleReasonTextForExchange },
        ];

        reasons.forEach(reason => {
            if (reason.text !== null && reason.text !== 'null') {
                if (reason.id) {
                    addRowWithBulletPoint(reason.id, reason.text);
                } else {
                    addSimpleRow(reason?.text);
                }
            }
        });
    }

    return (
        <table className='margin-left-4'>
            <tbody>{result}</tbody>
        </table>
    );
};

const renderAnnualBenchmarkPremiumBtn = (valueInDollars: string, handleClick: () => void, stateCode?: string) => (
    <span className="aptc-details__value">
        <ConditionalRendering isTrue={valueInDollars !== 'N/A' && stateCode !== states.NEW_JERSEY}>
            <GIButton
                id="total-annual-slcsp-view"
                labelId={`${valueInDollars}`}
                variant="link"
                onClickHandler={handleClick}
            />
        </ConditionalRendering>
        <ConditionalRendering isTrue={valueInDollars === 'N/A' || stateCode === states.NEW_JERSEY}>
            {valueInDollars}
        </ConditionalRendering>
    </span>
);

const mapApctDetails = (
    item: { aptcDetails: { [s: string]: unknown; monthlySlcspPremium: [] }, eligiblieForStateSubsidy: boolean, zipCode: string },
    stateSubsidyModel?: StateSubsidyModel | string,
    stateSubsidyApplicable?: boolean,
    stateCode?: string,
    openModal?: (modalId: string, props?: any) => void,
) => {
    // State Subsidy figures are only shown when the configuration enables State Subsidy
    // (HIX-233726), a State Subsidy model is configured (HIX-234502), and the household
    // is eligible for it.
    const showStateSubsidy =
        !!stateSubsidyApplicable && hasStateSubsidyModel(stateSubsidyModel) && !!item.eligiblieForStateSubsidy;
    const isSlcspModel = isSlcspMultiplierModel(stateSubsidyModel);
    const result: any[] = [];

    const handleClick = () => {
        openModal?.('monthlySlcspPremiumModal', { monthlySlcspPremium: item.aptcDetails?.monthlySlcspPremium ?? [] });
    };

    if (!item?.aptcDetails) {
        return (
            <div className="aptc-details">
                <FormattedMessage id={NA_MESSAGE_ID} />
            </div>
        );
    }

    const pushRow = (key: string, labelId: string, value: React.ReactNode) => {
        result.push(
            <div className="aptc-details__row" key={key}>
                <span className="aptc-details__label">
                    <FormattedMessage id={labelId} />
                </span>
                <span className="aptc-details__value">{value}</span>
            </div>,
        );
    };

    Object.entries(item.aptcDetails).forEach((details: any[]) => {
        const valueInDollars = !isNaN(details[1]) ? `$${details[1]}` : details[1];
        const valuesInTwoDecimal = !isNaN(details[1]) ? Number(details[1]).toFixed(2) : details[1];
        const valueInMonths = !isNaN(details[1]) ? `${details[1]}` : details[1];
        switch (details[0]) {
            case 'annualBenchmarkPremium':
                result.push(
                    <div className="aptc-details__row" key="annualBenchmarkPremium">
                        <span className="aptc-details__label">
                            <FormattedMessage id="application.eligibility.annualBenchmarkPremium" />
                        </span>
                        {renderAnnualBenchmarkPremiumBtn(valueInDollars, handleClick, stateCode)}
                    </div>,
                );
                pushRow('zipcodeUsedForSlcsp', 'application.eligibility.zipcodeUsedForSlcsp', item.zipCode);
                break;
            case 'maxAnnualContribution':
                pushRow('maxAnnualContribution', 'application.eligibility.maxAnnualContribution', valueInDollars);
                break;
            case 'maxContributionPercentage':
                pushRow('maxContributionPercentage', 'application.eligibility.maxContributionPercentage', valuesInTwoDecimal);
                break;
            case 'annualConsumedAptc':
                pushRow('annualConsumedAptc', 'application.eligibility.annualConsumedAptc', valueInDollars);
                break;
            case 'remainingMonths':
                pushRow('remainingMonths', 'application.eligibility.remainingMonths', valueInMonths);
                break;
            case 'aptcAmount':
                pushRow('aptcAmount', 'application.eligibility.aptcAmount', valueInDollars);
                break;
            // Maximum Household Contribution figures are specific to the SLCSP Multiplier
            // model (PA/NM). The Flat model only surfaces the State Subsidy Amount.
            case 'maxHHContributionForSS':
                if (showStateSubsidy && isSlcspModel) {
                    pushRow('maxHHContributionForSS', 'application.eligibility.maxHHContributionForSS', valueInDollars);
                }
                break;
            case 'maxHHContributionPercentageForSS':
                if (showStateSubsidy && isSlcspModel) {
                    pushRow('maxHHContributionPercentageForSS', 'application.eligibility.maxHHContributionPercentageForSS', valuesInTwoDecimal);
                }
                break;
            case 'ssamount':
                // State Subsidy Amount is shown for both models (Flat and SLCSP Multiplier).
                if (showStateSubsidy) {
                    pushRow('ssamount', 'application.eligibility.ssamount', valueInDollars);
                }
                break;
            default:
                break;
        }
    });
    return (
        <div className="aptc-details">
            {result}
        </div>
    );
};
